import React from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';

import { getEnvVar } from 'utils/get-env-var';

import defaultShareImage from 'img/thank-you-thumb.jpg';

export const SITE_NAME = 'The JP Bouvet Method';

export const DEFAULT_DESCRIPTION = 'Online drum lessons for drum set improvisation and creative freedom. '
    + 'Learn how the JP Bouvet Method is helping drummers improvise faster and more easily than any other online resource.';

// og:image needs an absolute URL, so imported assets get prefixed with the site origin
const absoluteUrl = path => {
    if (!path) return null;
    if (path.indexOf('http') === 0) return path;

    return `${getEnvVar('SITE_URL')}${path.indexOf('/') === 0 ? '' : '/'}${path}`;
};

// search results truncate around 60 characters, so only append the site name when there is room
const buildTitle = title => {
    if (!title) return SITE_NAME;
    if (title.indexOf(SITE_NAME) !== -1) return title;
    if (title.length > 45) return title;

    return `${title} | ${SITE_NAME}`;
};

const Seo = ({
    title,
    description = DEFAULT_DESCRIPTION,
    image,
    imageAlt,
    // defaults to the current path — pass a path to override (eg. a canonical that differs from the URL)
    path,
    type = 'website',
    lang = 'en',
    noindex = false,
    // [{ lang: 'fr', path: '/delabatterie' }] renders hreflang alternates
    alternates,
    jsonLd,
    location
}) => {
    const pageTitle = buildTitle(title);
    const canonical = absoluteUrl(path || location.pathname);
    const shareImage = absoluteUrl(image || defaultShareImage);

    return (
        <Helmet>
            <html lang={lang} />

            <title>{pageTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />

            {noindex
                ? <meta name="robots" content="noindex, follow" />
                : <meta name="robots" content="index, follow, max-image-preview:large" />}

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={shareImage} />
            <meta property="og:image:alt" content={imageAlt || pageTitle} />
            <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={shareImage} />

            {(alternates || []).map(alternate => (
                <link
                    key={alternate.lang}
                    rel="alternate"
                    hrefLang={alternate.lang}
                    href={absoluteUrl(alternate.path)} />
            ))}

            {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        </Helmet>
    );
};

export default withRouter(Seo);
